import { supabase } from './supabase';

export interface ParentPortalChild {
  childId: string;
  displayName: string;
  levelCode: string;
  preferredLanguage: string;
  relationshipLabel: string;
  isPrimary: boolean;
  createdAt: string;
}

export interface ParentChildProgress {
  childId: string;
  displayName: string;
  levelCode: string;
  quizAttempts: number;
  weeklyQuizzes: number;
  quizAccuracyPercent: number;
  assignmentsDue: number;
  tableSessions: number;
  tableAccuracyPercent: number;
  secureFacts: number;
  masteredFacts: number;
  wordSessions: number;
  wordAccuracyPercent: number;
  learningWords: number;
  masteredWords: number;
  lastActivityAt: string | null;
}

function throwIfError(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

export async function listParentPortalChildren(): Promise<ParentPortalChild[]> {
  const { data, error } = await supabase.rpc('list_parent_portal_children');
  throwIfError(error);
  return (data ?? []).map((row: Record<string, unknown>) => ({
    childId: String(row.child_id),
    displayName: String(row.display_name ?? 'Learner'),
    levelCode: String(row.level_code ?? 'C1'),
    preferredLanguage: String(row.preferred_language ?? 'en'),
    relationshipLabel: String(row.relationship_label ?? 'guardian'),
    isPrimary: Boolean(row.is_primary),
    createdAt: String(row.created_at ?? ''),
  }));
}

export async function loadParentChildProgress(childId: string): Promise<ParentChildProgress> {
  const { data, error } = await supabase.rpc('get_parent_child_progress', { p_child_id: childId });
  throwIfError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No progress summary was returned for this child.');
  return {
    childId: String(row.child_id),
    displayName: String(row.display_name ?? 'Learner'),
    levelCode: String(row.level_code ?? 'C1'),
    quizAttempts: Number(row.quiz_attempts ?? 0),
    weeklyQuizzes: Number(row.weekly_quizzes ?? 0),
    quizAccuracyPercent: Number(row.quiz_accuracy_percent ?? 0),
    assignmentsDue: Number(row.assignments_due ?? 0),
    tableSessions: Number(row.table_sessions ?? 0),
    tableAccuracyPercent: Number(row.table_accuracy_percent ?? 0),
    secureFacts: Number(row.secure_facts ?? 0),
    masteredFacts: Number(row.mastered_facts ?? 0),
    wordSessions: Number(row.word_sessions ?? 0),
    wordAccuracyPercent: Number(row.word_accuracy_percent ?? 0),
    learningWords: Number(row.learning_words ?? 0),
    masteredWords: Number(row.mastered_words ?? 0),
    lastActivityAt: (row.last_activity_at as string | null | undefined) ?? null,
  };
}

export async function createParentChildProfile(
  displayName: string,
  levelCode: string,
  preferredLanguage: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('create_parent_child_profile', {
    p_display_name: displayName,
    p_level_code: levelCode,
    p_preferred_language: preferredLanguage,
  });
  throwIfError(error);
  if (!data) throw new Error('The child profile could not be created.');
  return data as string;
}

export async function updateParentChildProfile(
  childId: string,
  displayName: string,
  levelCode: string,
  preferredLanguage: string,
): Promise<void> {
  const { error } = await supabase.rpc('update_parent_child_profile', {
    p_child_id: childId,
    p_display_name: displayName,
    p_level_code: levelCode,
    p_preferred_language: preferredLanguage,
  });
  throwIfError(error);
}

export async function enrollChildInClass(joinCode: string, childId: string): Promise<string> {
  const { data, error } = await supabase.rpc('enroll_child_in_class_by_code', {
    p_join_code: joinCode,
    p_child_id: childId,
  });
  throwIfError(error);
  if (!data) throw new Error('The learner could not be enrolled in the class.');
  return String(data);
}
